import { BookMessageConverter } from './components/book/message-converter.service';
import { ParserModule } from './modules/parser/parser.module';
import { APP_INITIALIZER, ErrorHandler, Inject, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { __config_seco_piloting } from './redux/reducers';

import {
  CONFIG_TOKEN,
  IdGenerationModule,
  ReefI18nModule,
  HTTP_CONFIG_TOKEN,
  ReefAppModule,
  ReefGatewayService,
  ReefTokenService,
  ReefKeepAliveService
} from '@seco/core';
import { AnalyticsModule, HttpModule as SecoHttpModule } from '@seco/dev-utils';

import { NgbCompatModule } from '@uxdf/direct-shared-lib';
import { NgSelectModule } from '@ng-select/ng-select';

import { AppRoutingModule } from './app-routing.module';
import { PermissionGuard } from './guards/permission.guard';

import { IsDecisionMakerGuard } from './guards/IsDecisionMakerGuard.service';

import {
  DfMonthPickerModule,
  ErrorsHandler,
  ExceptionsModule,
  getDirectLogin,
  HeaderAlertModule,
  LanguageService,
  LoadingErrorModule,
  LoginModule,
  LoginService,
  MenuModule,
  PipesModule,
  SpinnerModule,
  SsoService,
  StoreProviderModule,
  TokenInterceptor,
  WINDOW_MESSAGE_CONVERTERS,
  ConfigService
} from '@uxdf/direct-shared-lib';
import { ConfirmAlertModule, FooterModule, FormGroupModule, NavbarModule, TileModule } from '@uxdf/p-components';

import { NgxPageScrollModule } from 'ngx-page-scroll';
import { GuestGuard } from './guards/guest.guard';

import { TokenPermissionGuard } from './guards/token-permission.guard';

import { CustomerConfigGuard } from './guards/customer-config.guard.service';

import { RegistrationGuard } from './guards/registration.guard';
import { AgreementGuard } from './guards/agreement.guard';
import { AccessGuard } from './guards/access.guard';

import { AvailableGuard } from './guards/available.guard';

import { DfSelectModule, DfMediaModule, DfAlertModule } from '@design-factory/design-factory';

import { LoyaltyAlertService } from './redux/services/LoyaltyAlertService';
import { AppComponent } from './app.component';
import { AuthGuard } from './guards/auth.guard';
import { CookieService } from 'ngx-cookie-service';
import { LoginModule as SecoLoginModule } from '@seco/login';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule as NgxTranslateModule,
  TranslateService as NgxTranslateService
} from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { CsvModule } from '@ctrl/ngx-csv';
import { PdfService as DashboardPdfService } from './redux/services/dashboard/pdf.service';
import { CsvService } from './redux/services/dashboard/csv.service';

import { API_CONFIG } from './configs/api.config';
import { EDENRED_COUNTRY_CONFIG } from './configs/edenred-country-config';
import { CustomPreloadingStrategyService } from './guards/preload.guard';
import { EPowerComponent } from './components/book/e-power/e-power.component';
import { SecoPilotingConfig, SecoPilotingModule } from '@uxdf/seco-piloting';
import { defer, Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { UserIdleModule } from 'angular-user-idle';

import { PermissionHelper } from './redux/services/utils/permission-helper.service';
import { PermissionsConfigGuard } from './guards/permissions-config.guard.service';
import { EnvModule } from '../environments/env.module';
import { E2EModule } from '../environments/e2e/e2e.module';
import { TabbedViewModule, TitleBarModule } from '@uxdf/direct-shared-lib/src/lib/components';

import { set as featureTogglesSet } from 'feature-toggle-service';
import { FeatureToggleModule } from 'ngx-feature-toggle';
import { featureTogglesConfig } from './configs/feature-toggles.config';
import { LocaleManagerModule, LocaleResolverService, LocaleWatcherService } from '@uxdf/locale-manager';

import { LogoutComponent } from './components/logout/logout.component';
import { DoughnutChartModule } from './directives/doughnutChart.module';
import { AccountModule } from './components/account/account.module';
import { BookModule } from './components/book/book.module';
import { DashboardModule } from './components/dashboard/dashboard.module';
import { HomepageModule } from './components/homepage/homepage.module';
import { LoyaltyModule } from './components/loyalty/loyalty.module';
import { NG_SCROLLBAR_OPTIONS, NgScrollbarModule } from 'ngx-scrollbar';
import { DashboardAVCModule } from './components/dashboard/dashboard-avc/dashboard-avc.module';
import { AvcCreditLineModule } from './components/avc-credit-line/avc-credit-line.module';
import { AdHomePageReloadFromAshGuard } from './guards/AdHomePageReloadFromAshGuard';

let featureToggles = { ...featureTogglesConfig };
if (window.URL) {
  const url = new URL(window.location.href);
  featureToggles = Object.keys(featureTogglesConfig).reduce(
    (acc, key) => ({
      ...acc,
      [key]: url.searchParams.has(key) || featureTogglesConfig[key]
    }),
    {}
  );
}

featureTogglesSet(featureToggles);

export class UmTranslateHttpLoader implements TranslateLoader {
  constructor(private readonly http: HttpClient) {}
  /**
   * Gets the translations from the server
   */
  /* eslint-disable */
  getTranslation(lang: string): Observable<Object> {
    return this.http.get(`/assets/i18n/user-management/${lang}.json`);
  }
}

@NgModule({
  imports: [
    BrowserModule,
    CommonModule,
    HttpClientModule,
    AppRoutingModule,
    FeatureToggleModule,
    LoginModule,
    SecoLoginModule,
    SecoHttpModule,
    LoyaltyModule,
    NgScrollbarModule,
    NgxTranslateModule.forRoot({
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      },
      loader: {
        // TODO: Harmonize confiruation for seco http module and TranslateLoader
        provide: TranslateLoader,
        useClass: UmTranslateHttpLoader,
        deps: [HttpClient]
      }
    }),
    NgxPermissionsModule.forRoot(),
    IdGenerationModule,
    NgbCompatModule,
    TileModule,
    NavbarModule,
    FooterModule,
    ConfirmAlertModule,
    FormGroupModule,
    SpinnerModule,
    LoadingErrorModule,
    PipesModule,
    NgSelectModule,
    ReactiveFormsModule,
    FormsModule,
    HeaderAlertModule,
    TitleBarModule,
    TabbedViewModule,
    NgxPageScrollModule,
    StoreProviderModule,
    MenuModule,
    SecoPilotingModule,
    ExceptionsModule,
    DfSelectModule,
    DfMonthPickerModule,
    CsvModule,
    ParserModule,
    UserIdleModule.forRoot({ idle: 900, timeout: 30, ping: 600 }),
    EnvModule,
    E2EModule,
    LocaleManagerModule.forRoot(),
    ReefI18nModule.forRoot(),
    DfMediaModule,
    ReefAppModule.forRoot(),
    AnalyticsModule,
    DoughnutChartModule,
    DashboardAVCModule,
    DfAlertModule,
    AvcCreditLineModule,
    AccountModule,
    BookModule,
    DashboardModule,
    HomepageModule,
    LoyaltyModule,
    AvcCreditLineModule
  ],
  declarations: [AppComponent, LogoutComponent],
  providers: [
    provideNoopAnimations(),
    CustomPreloadingStrategyService,
    PermissionGuard,
    AuthGuard,
    AdHomePageReloadFromAshGuard,
    CookieService,
    TokenPermissionGuard,
    IsDecisionMakerGuard,
    NgxPermissionsService,
    SsoService,
    CustomerConfigGuard,
    LanguageService,
    LoginService,
    RegistrationGuard,
    AgreementGuard,
    AccessGuard,
    GuestGuard,
    AvailableGuard,
    LoyaltyAlertService,
    DashboardPdfService,
    PermissionsConfigGuard,
    PermissionHelper,
    CsvService,
    NgxTranslateService,
    LocaleWatcherService,
    ReefGatewayService,
    ReefTokenService,
    ReefKeepAliveService,
    { provide: SecoPilotingConfig, useFactory: initSecoPiloting, deps: [Store, LOCALE_ID, LocaleResolverService] },
    { provide: ErrorHandler, useClass: ErrorsHandler },
    { provide: CONFIG_TOKEN, useValue: { clpAppId: 'MMM', language: 'en_gb' } },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: 'API', useValue: API_CONFIG },
    { provide: 'EdenredSupportedCountries', useValue: EDENRED_COUNTRY_CONFIG },
    { provide: APP_INITIALIZER, useFactory: initConfigLoad, deps: [ConfigService], multi: true },
    { provide: APP_INITIALIZER, useFactory: initAuth, deps: [LoginService], multi: true },
    {
      provide: HTTP_CONFIG_TOKEN,
      useValue: {
        siteCode: 'XADIXADI',
        language: 'GB',
        baseUrl: '/app_amadeus_direct'
      }
    },
    {
      provide: APP_BASE_HREF,
      useValue: '/'
    },
    {
      provide: WINDOW_MESSAGE_CONVERTERS,
      useClass: BookMessageConverter,
      multi: true
    },
    { provide: NG_SCROLLBAR_OPTIONS, useValue: { visibility: 'always', appearance: 'standard' } }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    @Inject(CONFIG_TOKEN) config: any,
    private readonly store: Store<any>,
    private readonly loginService: LoginService,
    private readonly localeWatcher: LocaleWatcherService,
    private readonly ngxTranslater: NgxTranslateService,
    private readonly dslLanguageService: LanguageService,
    private readonly configService: ConfigService
  ) {
    config.clpConfig = this.store.pipe(select(getDirectLogin));
    config.onSuccess = this.loginService.directLoginPipeline.bind(this.loginService);
    this.localeWatcher.startWatch(ngxTranslater, dslLanguageService);
  }
}

export function initAuth(loginService: LoginService) {
  return () => loginService.restoreToken();
}

export function initConfigLoad(configService: ConfigService) {
  return () => configService.loadConfiguration();
}

export function initSecoPiloting(store: Store<any>, bcpLocale: string, localeResolver: LocaleResolverService) {
  const loginUrl = store
    .pipe(
      select(__config_seco_piloting),
      filter(({ secoUrl }) => !!secoUrl),
      map(({ secoUrl }) => secoUrl),
      take(1)
    )
    .toPromise();
  /* eslint-disable */

  const externalCatalogUrl =
    'https://pdt.accounts.sellingplatformconnect.amadeus.com/LoginService/authorize?service=SECO_SSO&prompt=none&nonce=jhsd&response_mode=query&redirect_uri=https%3A%2F%2Facceptance.sellingplatformconnect.amadeus.com%2Flogin%2FloginFromSAMLSSO%3FSITE%3DLOGINURL%26LANGUAGE%3DGB%26nonce%3Djhsd%26OV_SITE_SMART_PORTAL_URL';
  const language = defer(() => of(localeResolver.resolveFromBcpToSi(bcpLocale)));
  return new SecoPilotingConfig({
    loginUrl,
    externalCatalogUrl,
    language
  });
}
